import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Eye, EyeOff } from 'lucide-react';
import PrayerTimesPanel from './PrayerTimesPanel';
import TwilightTimesPanel from './TwilightTimesPanel';
import SpecialTimesPanel from './SpecialTimesPanel';
import WeatherPanel from './WeatherPanel';

const AdditionalInfoContainer = ({
  coordinates,
  additionalInfo,
  onPrayerTimesLoaded,
  showPrayerMarkers,
  setShowPrayerMarkers,
  forceExpanded = false,
  timezone = null,
  weatherData = null,
  weatherLoading = false
}) => {
  const [expanded, setExpanded] = useState(forceExpanded);
  const [nextPrayer, setNextPrayer] = useState(null);
  const [timeUntilNextPrayer, setTimeUntilNextPrayer] = useState('');
  const [showWeather, setShowWeather] = useState(() => {
    const saved = localStorage.getItem('sunriseApp.showWeather');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showTwilight, setShowTwilight] = useState(() => {
    const saved = localStorage.getItem('sunriseApp.showTwilight');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [showSpecial, setShowSpecial] = useState(() => {
    const saved = localStorage.getItem('sunriseApp.showSpecial');
    return saved !== null ? JSON.parse(saved) : false;
  });

  const toggleWeather = () => {
    const next = !showWeather;
    setShowWeather(next);
    localStorage.setItem('sunriseApp.showWeather', JSON.stringify(next));
  };

  const toggleTwilight = () => {
    const next = !showTwilight;
    setShowTwilight(next);
    localStorage.setItem('sunriseApp.showTwilight', JSON.stringify(next));
  };

  const toggleSpecial = () => {
    const next = !showSpecial;
    setShowSpecial(next);
    localStorage.setItem('sunriseApp.showSpecial', JSON.stringify(next));
  };

  // This method will be called by the PrayerTimesPanel to update header info
  const updateNextPrayerInfo = (prayer, timeRemaining) => {
    if (!prayer || !timeRemaining) return;

    // Only update if values have changed to avoid infinite loops
    if ((!nextPrayer || nextPrayer.name !== prayer.name) ||
        timeUntilNextPrayer !== timeRemaining) {
      setNextPrayer(prayer);
      setTimeUntilNextPrayer(timeRemaining);
    }
  };

  // If no additional info is available
  if (!additionalInfo && !coordinates) {
    return (
      <div className="w-full mt-6 bg-white/10 backdrop-blur-sm rounded-lg overflow-hidden shadow-md">
        <button
          className="w-full flex items-center justify-between p-3 text-white hover:bg-white/10 transition-colors"
          onClick={() => setExpanded(!expanded)}
        >
          <span className="font-medium">Additional Information</span>
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>

        {expanded && (
          <div className="p-4 border-t border-white/10 text-center">
            <p className="text-white/70">
              Switch to API mode to see additional information including prayer times and twilight times.
            </p>
          </div>
        )}
      </div>
    );
  }

  const isOpen = forceExpanded || expanded;

  return (
    <div className={`w-full ${forceExpanded ? '' : 'mt-6'} bg-white/10 backdrop-blur-sm rounded-lg overflow-hidden shadow-md`}>
      {!forceExpanded && (
        <button
          className="w-full flex items-center justify-between p-3 text-white hover:bg-white/10 transition-colors min-h-[44px]"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
        >
          <div className="flex items-center">
            <span className="font-medium">Additional Information</span>
            {nextPrayer && (
              <span className="ml-2 text-xs bg-white/20 px-2 py-0.5 rounded">
                Next: {nextPrayer.name} ({timeUntilNextPrayer || '--'})
              </span>
            )}
          </div>
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
      )}

      {isOpen && (
        <div className={`p-4 ${forceExpanded ? '' : 'border-t border-white/10'}`}>
          {/* Prayer Times — always visible */}
          <PrayerTimesPanel
            coordinates={coordinates}
            onPrayerTimesLoaded={onPrayerTimesLoaded}
            showPrayerMarkers={showPrayerMarkers}
            setShowPrayerMarkers={setShowPrayerMarkers}
            onNextPrayerUpdate={updateNextPrayerInfo}
          />

          {/* Weather — toggleable */}
          <div className="mb-2">
            <button
              className="w-full flex items-center justify-between py-2 group"
              onClick={toggleWeather}
            >
              <h3 className="text-white/80 font-medium text-sm">Weather</h3>
              {showWeather
                ? <EyeOff size={14} className="text-white/40 group-hover:text-white/70 transition-colors" />
                : <Eye size={14} className="text-white/40 group-hover:text-white/70 transition-colors" />
              }
            </button>
            {showWeather && (
              <WeatherPanel weatherData={weatherData} weatherLoading={weatherLoading} />
            )}
          </div>

          {/* Twilight Times — toggleable */}
          {additionalInfo && (
            <div className="mb-2">
              <button
                className="w-full flex items-center justify-between py-2 group"
                onClick={toggleTwilight}
              >
                <h3 className="text-white/80 font-medium text-sm">Twilight Times</h3>
                {showTwilight
                  ? <EyeOff size={14} className="text-white/40 group-hover:text-white/70 transition-colors" />
                  : <Eye size={14} className="text-white/40 group-hover:text-white/70 transition-colors" />
                }
              </button>
              {showTwilight && (
                <TwilightTimesPanel additionalInfo={additionalInfo} coordinates={coordinates} timezone={timezone} />
              )}
            </div>
          )}

          {/* Special Times — toggleable */}
          {additionalInfo && (
            <div className="mb-2">
              <button
                className="w-full flex items-center justify-between py-2 group"
                onClick={toggleSpecial}
              >
                <h3 className="text-white/80 font-medium text-sm">Special Times</h3>
                {showSpecial
                  ? <EyeOff size={14} className="text-white/40 group-hover:text-white/70 transition-colors" />
                  : <Eye size={14} className="text-white/40 group-hover:text-white/70 transition-colors" />
                }
              </button>
              {showSpecial && (
                <SpecialTimesPanel additionalInfo={additionalInfo} coordinates={coordinates} timezone={timezone} />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdditionalInfoContainer;
